import type { PendingItem, PendingSummary, TaskListResponse } from "@/domain/task";
import { ASSIGNEE_ME, UNASSIGNED_KEY } from "@/domain/task";
import { getUserStorageService } from "@/infrastructure/di";
import { InvalidationEntity } from "../entityDependencies";
import { queryKeys } from "../queryKeys";
import { isObject } from "../cache/cacheSlots";
import { aggregateSlot, flatAggregates, type AggregateDelta } from "../cache/aggregateSlots";
import { defineEntityCache } from "../cache/entityCache";
import {
  distinctPages,
  isActive,
  viewSlot,
  type Compare,
  type ViewRules,
  type ViewShape,
} from "../cache/listViews";

export type TaskPatch = Partial<Pick<PendingItem, "assignee">>;

type TaskPage = Pick<TaskListResponse, "items" | "total_count">;

const page: ViewShape<TaskPage, PendingItem, string> = {
  isPage: (value): value is TaskPage =>
    isObject(value) && Array.isArray(value.items) && typeof value.total_count === "number",
  records: (current) => current.items,
  withRecords: (current, items, countDelta) => ({
    ...current,
    items,
    total_count: current.total_count + countDelta,
  }),
  idOf: (item) => item.code,
  total: (current) => current.total_count,
};

export const distinctTaskPages = distinctPages(page);

const merge = (item: PendingItem, patch: TaskPatch): PendingItem => ({ ...item, ...patch });

function assigneeAccepts(value: unknown, item: PendingItem) {
  if (!isActive(value) || !Array.isArray(value)) return true;
  const assignee = item.assignee?.uuid ?? null;
  if (assignee === null) return value.includes(UNASSIGNED_KEY);
  if (value.includes(assignee)) return true;
  if (!value.includes(ASSIGNEE_ME)) return false;
  const me = getUserStorageService().getCurrentUser()?.id;
  return me === undefined ? undefined : me === assignee;
}

const byObjectThenQueue: Compare<PendingItem> = (a, b) => {
  const left = `${a.object.uuid.toLowerCase()}\u0000${a.queue}`;
  const right = `${b.object.uuid.toLowerCase()}\u0000${b.queue}`;
  if (left === right) return 0;
  return left < right ? -1 : 1;
};

const time = (value: string | null) => (value ? Date.parse(value) : null);

export const taskRules: ViewRules<PendingItem> = {
  accepts: { assignee: assigneeAccepts },
  narrowing: ["search", "domain", "queue", "urgency", "due_from", "due_to"],
  runs: {
    assignee: (item) => item.assignee?.uuid ?? null,
    queue: (item) => item.queue,
    domain: (item) => item.domain,
  },
  order: {
    urgency: (item) => (item.urgency === "urgent" ? 1 : 0),
    due_at: (item) => time(item.due_at),
  },
  tieBreak: byObjectThenQueue,
};

const aggregates = flatAggregates<PendingItem>({
  sums: { "amount:sum": (item) => item.amount },
  counts: {
    "assignee:filled": (item) => item.assignee !== null,
    "assignee:empty": (item) => item.assignee === null,
  },
  distributions: {
    "queue:distribution": (item) => [item.queue],
    "domain:distribution": (item) => [item.domain],
    "urgency:distribution": (item) => [item.urgency],
    "assignee:distribution": (item) => [item.assignee?.uuid ?? UNASSIGNED_KEY],
  },
  emptyDistribution: {},
});

const isSnoozed = (item: PendingItem) =>
  item.snooze_expires_at !== null && Date.parse(item.snooze_expires_at) > Date.now();

function isSummary(value: unknown): value is PendingSummary {
  return isObject(value) && Array.isArray(value.queues) && typeof value.pending_items === "number";
}

function summaryDelta(data: PendingSummary, delta: AggregateDelta<PendingItem>): PendingSummary {
  const moves = [
    ...(delta.removed && !isSnoozed(delta.removed) ? [{ item: delta.removed, sign: -1 }] : []),
    ...(delta.added && !isSnoozed(delta.added) ? [{ item: delta.added, sign: 1 }] : []),
  ];
  if (moves.length === 0) return data;
  let queues = data.queues;
  for (const { item, sign } of moves) {
    const urgent = item.urgency === "urgent" ? sign : 0;
    const current = queues.find((entry) => entry.queue === item.queue);
    if (!current && sign > 0) return data;
    queues = queues
      .map((entry) =>
        entry.queue === item.queue
          ? { ...entry, total: entry.total + sign, urgent: entry.urgent + urgent }
          : entry,
      )
      .filter((entry) => entry.total > 0);
  }
  const pending = queues.reduce((sum, entry) => sum + entry.total, 0);
  const urgent = queues.reduce((sum, entry) => sum + entry.urgent, 0);
  if (pending === data.pending_items && urgent === data.urgent_items) return data;
  return { ...data, queues, pending_items: pending, urgent_items: urgent };
}

/** Pending items, keyed by their code: their lists, footers and the org-wide summary badge. */
export const taskCache = defineEntityCache<string, TaskPatch, PendingItem>({
  entity: InvalidationEntity.TASK,
  row: merge,
  slots: (orgId) => [
    viewSlot({
      prefix: queryKeys.tasks.lists(orgId),
      shape: page,
      merge,
      parse: queryKeys.tasks.parseList,
      rules: taskRules,
    }),
    aggregateSlot({
      prefix: queryKeys.tasks.aggregatesAll(orgId),
      rules: taskRules,
      parse: queryKeys.tasks.parseAggregates,
      ...aggregates,
    }),
    aggregateSlot({
      prefix: queryKeys.tasks.summary(orgId),
      rules: taskRules,
      parse: queryKeys.tasks.parseSummary,
      isData: isSummary,
      apply: summaryDelta,
    }),
  ],
  records: () => [],
});
